<script>
import { ref, computed, watch, onMounted } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { required, requiredIf, email, minLength, maxLength, helpers } from '@vuelidate/validators'
import { 
  CheckCircleIcon, 
  XCircleIcon, 
  UserIcon, 
  EnvelopeIcon, 
  PhoneIcon, 
  UserGroupIcon, 
  CalendarIcon, 
  ClockIcon, 
  ChatBubbleLeftIcon, 
  PencilIcon, 
  TrashIcon, 
  XMarkIcon 
} from '@heroicons/vue/24/outline'
import { 
  updateCustomerReservationFn, 
  customerReservationFn, 
  cancelCustomerReservationFn,
  updatePublicReservationFn, 
  cancelPublicReservationFn,
  getCustomerInfoFn
} from '@/services/api/restApiCalls'
import { useAuthGlobalStore } from '@/stores/authGlobalStore'
import { useThemeStore } from '@/stores/themeStore'
import { useRouter } from 'vue-router'
import LoadingSpinner from '@/components/common/LoadingSpinner/LoadingSpinner.vue'
import { notify } from 'notiwind'
import { useDateTime } from '@/composables/useDateTime'
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue'
import MobileTimePicker from '@/composables/MobileTimePicker.vue'
import VueDatePicker from '@vuepic/vue-datepicker'

export default {
  props: {
    reservation: {
      type: Object,
      default: null
    },
    isPublic: {
      type: Boolean,
      default: false
    },
    restoId: {
      type: [String, Number],
      required: true
    },
    showHeader: {
      type: Boolean,
      default: true
    }
  },
  emits: ['close', 'refresh'],
  components: {
    CheckCircleIcon,
    XCircleIcon,
    UserIcon,
    EnvelopeIcon,
    PhoneIcon,
    UserGroupIcon,
    CalendarIcon,
    ClockIcon,
    ChatBubbleLeftIcon,
    LoadingSpinner,
    PencilIcon,
    TrashIcon,
    XMarkIcon,
    Dialog,
    DialogPanel,
    TransitionChild,
    TransitionRoot,
    MobileTimePicker,
    VueDatePicker
  },
  setup(props, { emit }) {
    const authStore = useAuthGlobalStore()
    const themeStore = useThemeStore()
    const router = useRouter()
    const { formatDateTime } = useDateTime()

    // State
    const isEditMode = ref(false)
    const isLoading = ref(false)
    const isProcessing = ref(false)
    const showCancelModal = ref(false)
    const showConfirmModal = ref(false)
    const reservationInfo = ref({})
    const submitted = ref(false)
    
    // Form Data
    const name = ref('')
    const emailId = ref('')
    const phoneNumber = ref('')
    const date = ref('')
    const selectedTime = ref(null)
    const message = ref('')
    const selectedGuest = ref('2')
    const pendingReservationData = ref(null)

    // Options
    const guests = ref([
      { id: 1, value: '1' }, { id: 2, value: '2' }, { id: 3, value: '3' }, 
      { id: 4, value: '4' }, { id: 5, value: '5' }, { id: 6, value: '6' }, 
      { id: 7, value: '7' }, { id: 8, value: '8' }, { id: 9, value: '9' }, 
      { id: 10, value: '10' }, { id: 11, value: '11' }, { id: 12, value: '12' }, 
      { id: 13, value: '13' }, { id: 14, value: '14' }, { id: 15, value: '15' }
    ])
    
    const time_option = ref([
      { id: 1, time: '10:15 am', value: '10:15' }, { id: 2, time: '10:30 am', value: '10:30' },
      { id: 3, time: '10:45 am', value: '10:45' }, { id: 4, time: '11:00 am', value: '11:00' },
      { id: 5, time: '11:15 am', value: '11:15' }, { id: 6, time: '11:30 am', value: '11:30' },
      { id: 7, time: '11:45 am', value: '11:45' }, { id: 8, time: '12:00 pm', value: '12:00' },
      { id: 9, time: '12:15 pm', value: '12:15' }, { id: 10, time: '12:30 pm', value: '12:30' },
      { id: 11, time: '12:45 pm', value: '12:45' }, { id: 12, time: '1:00 pm', value: '13:00' },
      { id: 13, time: '1:15 pm', value: '13:15' }, { id: 14, time: '1:30 pm', value: '13:30' },
      { id: 15, time: '1:45 pm', value: '13:45' }, { id: 16, time: '2:00 pm', value: '14:00' },
      { id: 17, time: '2:15 pm', value: '14:15' }, { id: 18, time: '2:30 pm', value: '14:30' },
      { id: 19, time: '2:45 pm', value: '14:45' }, { id: 20, time: '3:00 pm', value: '15:00' },
      { id: 21, time: '3:15 pm', value: '15:15' }, { id: 22, time: '3:30 pm', value: '15:30' },
      { id: 23, time: '3:45 pm', value: '15:45' }, { id: 24, time: '4:00 pm', value: '16:00' },
      { id: 25, time: '4:15 pm', value: '16:15' }, { id: 26, time: '4:30 pm', value: '16:30' },
      { id: 27, time: '4:45 pm', value: '16:45' }, { id: 28, time: '5:00 pm', value: '17:00' },
      { id: 29, time: '5:15 pm', value: '17:15' }, { id: 30, time: '5:30 pm', value: '17:30' },
      { id: 31, time: '5:45 pm', value: '17:45' }, { id: 32, time: '6:00 pm', value: '18:00' },
      { id: 33, time: '6:15 pm', value: '18:15' }, { id: 34, time: '6:30 pm', value: '18:30' },
      { id: 35, time: '6:45 pm', value: '18:45' }, { id: 36, time: '7:00 pm', value: '19:00' },
      { id: 37, time: '7:15 pm', value: '19:15' }, { id: 38, time: '7:30 pm', value: '19:30' },
      { id: 39, time: '7:45 pm', value: '19:45' }, { id: 40, time: '8:00 pm', value: '20:00' },
      { id: 41, time: '8:15 pm', value: '20:15' }, { id: 42, time: '8:30 pm', value: '20:30' },
      { id: 43, time: '8:45 pm', value: '20:45' }, { id: 44, time: '9:00 pm', value: '21:00' },
      { id: 45, time: '9:15 pm', value: '21:15' }, { id: 46, time: '9:30 pm', value: '21:30' },
      { id: 47, time: '9:45 pm', value: '21:45' }, { id: 48, time: '10:00 pm', value: '22:00' },
      { id: 49, time: '10:15 pm', value: '22:15' }, { id: 50, time: '10:30 pm', value: '22:30' },
      { id: 51, time: '10:45 pm', value: '22:45' }, { id: 52, time: '11:00 pm', value: '23:00' }
    ])

    // Dates
    let today = new Date()
    let maxDate = new Date(Date.now() + 1.555e10)
    
    function getDateFormat(date) {
      let year = date.getFullYear()
      let month = String(date.getMonth() + 1).padStart(2, '0')
      let day = String(date.getDate()).padStart(2, '0')
      return `${year}-${month}-${day}`
    }

    function formatTimeForPicker(hours, minutes) {
      const period = hours >= 12 ? 'pm' : 'am'
      const displayHours = hours % 12 || 12
      return `${displayHours}:${String(minutes).padStart(2, '0')} ${period}`
    }

    function getCurrentTimeRounded(increment = 5) {
      const now = new Date()
      let hours = now.getHours()
      let minutes = Math.ceil(now.getMinutes() / increment) * increment
      if (minutes >= 60) {
        hours = (hours + 1) % 24
        minutes = 0
      }
      return formatTimeForPicker(hours, minutes)
    }

    function getTimeFromPicker(value) {
      if (!value) return ''
      if (typeof value === 'string') {
        const match = value.match(/^(\d{1,2}):(\d{2})\s*(am|pm)$/i)
        if (!match) return value
        let hours = parseInt(match[1], 10)
        const minutes = match[2]
        const period = match[3].toLowerCase()
        if (period === 'pm' && hours !== 12) hours += 12
        if (period === 'am' && hours === 12) hours = 0
        return `${String(hours).padStart(2, '0')}:${minutes}`
      }
      if (typeof value === 'object' && value.hours != null && value.minutes != null) {
        return `${String(value.hours).padStart(2, '0')}:${String(value.minutes).padStart(2, '0')}`
      }
      return ''
    }

    function getReservationDateTimeFromPicker() {
      return {
        date: date.value,
        time: getTimeFromPicker(selectedTime.value)
      }
    }

    function setDefaultDateTime() {
      date.value = getDateFormat(new Date())
      selectedTime.value = getCurrentTimeRounded()
    }
    
    const minReservationDate = ref(getDateFormat(today))
    const maxReservationDate = ref(getDateFormat(maxDate))
    const datePickerLocale = 'en-GB'
    const isDatePickerDark = computed(() => themeStore.theme === 'dark')

    // Determine current mode
    // If props.reservation is null -> Create Mode (for logged in users primarily)
    // If props.reservation is set -> View Mode
    const isCreateMode = computed(() => !props.reservation)
    
    // Validation — stable rule shape; requiredIf toggles create-only fields
    const validationRules = {
      name: { required: requiredIf(isCreateMode) },
      emailId: {
        required: requiredIf(isCreateMode),
        email: helpers.withMessage('Value is not a valid email address', (value) => {
          if (!isCreateMode.value) return true
          return email.$validator(value)
        })
      },
      phoneNumber: {
        required: requiredIf(isCreateMode),
        minLength: helpers.withMessage('This field should be at least 10 characters long', (value) => {
          if (!isCreateMode.value) return true
          return minLength(10).$validator(value)
        }),
        maxLength: helpers.withMessage('The maximum length allowed is 15', (value) => {
          if (!isCreateMode.value) return true
          return maxLength(15).$validator(value)
        })
      },
      selectedGuest: { required },
      date: { required },
      selectedTime: { required }
    }
    
    const v$ = useVuelidate(validationRules, {
      name,
      emailId,
      phoneNumber,
      selectedGuest,
      date,
      selectedTime
    })

    const datePickerInputClass = computed(() =>
      submitted.value && (v$.value.date.$error || v$.value.selectedTime.$error)
        ? 'reservation-datepicker reservation-datepicker--error'
        : 'reservation-datepicker'
    )

    // Populate data from reservation prop
    const populateFormData = () => {
      if (props.reservation) {
        name.value = props.reservation.name || ''
        emailId.value = props.reservation.email || ''
        phoneNumber.value = props.reservation.phone_number || ''
        selectedGuest.value = String(props.reservation.guests || '2')
        message.value = props.reservation.message || ''
        
        if (props.reservation.requested_datetime) {
          const isoString = props.reservation.requested_datetime.replace(' ', 'T') + ':00'
          const reservationDate = new Date(isoString)
          if (!isNaN(reservationDate.getTime())) {
            date.value = getDateFormat(reservationDate)
            selectedTime.value = formatTimeForPicker(
              reservationDate.getUTCHours(),
              reservationDate.getUTCMinutes()
            )
          } else {
            setDefaultDateTime()
          }
        }
      } else {
        // Reset or load customer info if logged in
        resetForm()
        if (authStore.isLoggedIn && !props.isPublic) {
          loadCustomerInfo()
        }
      }
    }

    const loadCustomerInfo = async () => {
      try {
        const customer_info = await getCustomerInfoFn()
        name.value = customer_info?.first_name + ' ' + customer_info?.last_name
        emailId.value = customer_info?.email
        phoneNumber.value = customer_info?.phone_number
      } catch (e) {
        // fail silently
      }
    }

    const resetForm = () => {
      name.value = ''
      emailId.value = ''
      phoneNumber.value = ''
      selectedGuest.value = '2'
      setDefaultDateTime()
      message.value = ''
      submitted.value = false
      reservationInfo.value = {}
      v$.value.$reset()
    }

    // Handlers
    const handleEditClick = () => {
      populateFormData()
      isEditMode.value = true
      // Scroll to form
      setTimeout(() => {
        const formElement = document.getElementById('reservation-form')
        if (formElement) formElement.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
    }

    const cancelEdit = () => {
      isEditMode.value = false
      populateFormData() // Reset to original values
    }

    const handleCancelClick = () => {
      showCancelModal.value = true
    }

    const showConfirmationModal = () => {
      reservationInfo.value = ''
      submitted.value = true
      v$.value.$validate()
      
      if (v$.value.$invalid) return
      
      const { date: reservationDate, time: reservationTime } = getReservationDateTimeFromPicker()

      pendingReservationData.value = {
        name: name.value,
        email: emailId.value,
        phone: phoneNumber.value,
        guests: selectedGuest.value,
        date: reservationDate,
        time: reservationTime,
        message: message.value
      }
      showConfirmModal.value = true
    }

    // Main Action: Create or Update
    const handleSubmit = async () => {
      if (isLoading.value) return
      isLoading.value = true
      showConfirmModal.value = false
      
      try {
        let result
        const formData = new FormData()
        const { date: reservationDate, time: reservationTime } = getReservationDateTimeFromPicker()
        
        if (isCreateMode.value) {
            // Create New Reservation
            formData.append('name', name.value)
            formData.append('email', emailId.value)
            formData.append('phone_number', phoneNumber.value)
            formData.append('guests', selectedGuest.value)
            formData.append('requested_datetime_str', reservationDate)
            formData.append('requested_time', reservationTime)
            formData.append('message', message.value)
            
            result = await customerReservationFn(props.restoId, formData)
            
        } else {
            // Update Existing Reservation
            formData.append('message', message.value)
            formData.append('requested_datetime_str', reservationDate)
            formData.append('requested_time', reservationTime)
            formData.append('guests', selectedGuest.value)
            
            if (props.isPublic) {
                 // Public Update using UUID
                 // We assume props.reservation has the UUID or we need to pass it? 
                 // In PublicReservationView we got JSON which likely has reservation_id or id, 
                 // but public endpoints usually need the UUID from the URL. 
                 
                 // Wait, updatePublicReservationFn takes (resto_id, uuid, form_data)
                 // The 'reservation' object from getPublicReservationFn usually contains the UUID if the backend returns it.
                 // If not, we rely on the parent to manage the UUID or maybe it's in the object.
                 // Usually for public view, the ID in the object might be internal ID, but the URL has UUID.
                 // Ideally the parent should pass the identifier needed for update.
                 // Let's assume reservation.uuid exists OR we use the id if the backend supports it.
                 // BUT for public update, we likely need the UUID from the URL.
                 // The PROPS don't have UUID passed explicitly aside from potential inside 'reservation'.
                 // Let's double check PublicReservationView. It computed UUID from route. 
                 // We might need to accept an 'identifier' prop or similar.
                 // However, let's assume reservation object has what we need or we pass it as a prop.
                 // Let's check the passed down reservation object structure from PublicReservationView.
                 // It passed `currentReservation`.
                 
                 // To be safe, let's add an explicit `reservationId` prop which can be UUID or ID depending on context.
                 // OR we just use `props.reservation.uuid` / `props.reservation.id`.
                 // Public API uses UUID. Internal API uses ID.
                 
                 // NOTE: In `PublicReservationView.vue` we were using `uuid` from route.
                 // We should probably pass that UUID as a prop to this component to be robust.
            }
             
             // Check if we need to distinguish between ID and UUID for update
             const idToUse = props.isPublic ? (props.reservation.uuid || props.reservation.reservation_uuid || router.currentRoute.value.params.uuid) : (props.reservation.reservation_id || props.reservation.id)
             
             if (props.isPublic) {
                 result = await updatePublicReservationFn(props.restoId, idToUse, formData)
             } else {
                 result = await updateCustomerReservationFn(props.restoId, idToUse, formData, router)
             }
        }

        reservationInfo.value = result
        
        if (result.id != null || (result.status && result.status !== 400)) {
           notify({
            group: 'successmessage',
            title: 'Success',
            text: result?.status_msg || (isCreateMode.value ? 'Reservation created successfully' : 'Reservation updated successfully')
          }, 2000)
          
          if (isCreateMode.value) {
             resetForm()
             loadCustomerInfo() // reload user info
              
             const newUuid = result?.uuid || result?.reservation?.uuid
             if (props.isPublic && newUuid) {
                 // Redirect to public view
                 router.push({
                     name: 'reservation_public',
                     params: { uuid: newUuid }
                 })
             } else {
                 emit('refresh')
             }
          } else {
             isEditMode.value = false
             emit('refresh')
          }
        } else {
           notify({
            group: 'errormessage',
            title: 'Error',
            text: result?.status_msg || 'Operation failed'
          }, 2000)
        }
        
      } catch (error) {
         notify({
            group: 'errormessage',
            title: 'Error',
            text: 'An error occurred'
          }, 2000)
      } finally {
        isLoading.value = false
      }
    }

    const confirmCancel = async () => {
      if (isProcessing.value) return
      isProcessing.value = true
      
      try {
         // Determine ID to use
         const idToUse = props.isPublic ? (router.currentRoute.value.params.uuid) : (props.reservation.reservation_id || props.reservation.id)
         
         let result
         if (props.isPublic) {
             result = await cancelPublicReservationFn(props.restoId, idToUse)
         } else {
             result = await cancelCustomerReservationFn(props.restoId, idToUse, router)
         }
         
         if (result.id != null) {
            notify({
                group: 'successmessage',
                title: 'Success',
                text: result?.status_msg || 'Reservation cancelled successfully'
            }, 2000)
            showCancelModal.value = false
            emit('refresh')
         } else {
            notify({
                group: 'errormessage',
                title: 'Error',
                text: result?.status_msg || 'Failed to cancel'
            }, 2000)
         }
      } catch (e) {
         notify({
            group: 'errormessage',
            title: 'Error',
            text: 'An error occurred while cancelling'
          }, 2000)
      } finally {
        isProcessing.value = false
        showCancelModal.value = false
      }
    }

    // Helpers
    const getStatusColor = (status) => {
      switch (status?.toUpperCase()) {
        case 'CONFIRMED': return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
        case 'PENDING': return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
        case 'CANCELLED': return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
        default: return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
      }
    }
    
    const isExpired = computed(() => {
        if (!props.reservation) return false
        if (props.reservation.has_expired) return true
        
        if (!props.reservation.requested_datetime) return false
        const reservationDate = new Date(props.reservation.requested_datetime)
        if (isNaN(reservationDate.getTime())) return false
        const now = new Date()
        return reservationDate < now
    })

    const canShowActions = computed(() => {
      if (!props.reservation || isCreateMode.value) return false
      
      const status = props.reservation.status?.toUpperCase()

      // Allow actions only if CONFIRMED or PENDING
      if (status !== 'CONFIRMED' && status !== 'PENDING') {
          return false
      }
      
      // Check expiry
      if (isExpired.value) {
          return false
      }
      
      return true
    })

    // Watchers & Mount
    watch(() => props.reservation, (newVal) => {
      // If switching from one reservation to another, or from create to view
      populateFormData()
      isEditMode.value = false
      reservationInfo.value = {}
      submitted.value = false
    }, { immediate: true })
    
    onMounted(() => {
        populateFormData()
    })

    return {
      isCreateMode,
      isEditMode,
      isLoading,
      isProcessing,
      showCancelModal,
      showConfirmModal,
      submitted,
      reservationInfo,
      pendingReservationData,
      name,
      emailId,
      phoneNumber,
      guests,
      date,
      selectedTime,
      selectedGuest,
      message,
      time_option,
      v$,
      minReservationDate,
      maxReservationDate,
      datePickerLocale,
      isDatePickerDark,
      datePickerInputClass,
      formatDateTime,
      getStatusColor,
      canShowActions,
      isExpired,
      handleEditClick,
      cancelEdit,
      handleCancelClick,
      showConfirmationModal,
      handleSubmit,
      confirmCancel,
      closeConfirmModal: () => showConfirmModal.value = false,
      closeCancelModal: () => showCancelModal.value = false
    }
  }
}
</script>

<template>
  <div class="space-y-6">
    
    <!-- Header Section -->
    <div v-if="showHeader" class="text-center mb-5">
      <h2 class="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100 transition-colors duration-300">
         <span v-if="!reservation">Reserve Table</span>
         <span v-else>Reservation Details</span>
      </h2>
      <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
         <span v-if="!reservation">Fill in your details to book a table</span>
         <span v-else>Manage your reservation</span>
      </p>
    </div>

    <!-- VIEW DETAILS MODE (Only when reservation exists and NOT in edit mode) -->
    <div v-if="reservation && !isEditMode" class="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 transition-colors duration-300">
        
        <!-- Pending Warning -->
        <div v-if="reservation.status?.toUpperCase() === 'PENDING'" class="mb-6 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg flex items-start">
             <div class="flex-shrink-0">
               <span class="text-yellow-400">⚠️</span>
             </div>
             <div class="ml-3">
               <h3 class="text-sm font-medium text-yellow-800 dark:text-yellow-300">Reservation Pending</h3>
               <div class="mt-2 text-sm text-yellow-700 dark:text-yellow-400">
                 <p>Your reservation request is not confirmed yet. The restaurant may cancel the request if they cannot fulfill it.</p>
               </div>
             </div>
        </div>
        
        <!-- ID & Status -->
        <div class="flex justify-between items-center mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
           <div>
              <p class="text-sm text-gray-500 dark:text-gray-400">Reservation ID</p>
              <p class="text-xl font-bold text-indigo-600 dark:text-indigo-400">#{{ reservation.reservation_id || reservation.id }}</p>
           </div>
           <span
              class="px-4 py-1.5 rounded-full text-sm font-medium uppercase"
              :class="getStatusColor(reservation.status)"
            >
              {{ reservation.status }}
            </span>
        </div>

        <!-- Info Grid -->
        <div class="grid md:grid-cols-2 gap-8">
             <div>
                <div class="flex items-start mb-4">
                   <div class="flex-shrink-0 bg-violet-100 dark:bg-violet-900/30 p-2 rounded-lg">
                      <CalendarIcon class="h-6 w-6 text-violet-600 dark:text-violet-400" />
                   </div>
                   <div class="ml-4">
                      <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Date & Time</p>
                      <p class="text-lg font-semibold text-gray-900 dark:text-gray-100">{{ formatDateTime(reservation.requested_datetime) }}</p>
                   </div>
                </div>

                <div class="flex items-start mb-4">
                   <div class="flex-shrink-0 bg-violet-100 dark:bg-violet-900/30 p-2 rounded-lg">
                      <UserGroupIcon class="h-6 w-6 text-violet-600 dark:text-violet-400" />
                   </div>
                   <div class="ml-4">
                      <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Guests</p>
                      <p class="text-lg font-semibold text-gray-900 dark:text-gray-100">{{ reservation.guests }} People</p>
                   </div>
                </div>
             </div>
             
             <div>
                <div class="flex items-start mb-4">
                   <div class="flex-shrink-0 bg-blue-100 dark:bg-blue-900/30 p-2 rounded-lg">
                      <UserIcon class="h-6 w-6 text-blue-600 dark:text-blue-400" />
                   </div>
                   <div class="ml-4">
                      <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Contact Name</p>
                      <p class="text-lg font-semibold text-gray-900 dark:text-gray-100">{{ reservation.name }}</p>
                   </div>
                </div>
                
                 <div class="flex items-start mb-4">
                   <div class="flex-shrink-0 bg-blue-100 dark:bg-blue-900/30 p-2 rounded-lg">
                      <ChatBubbleLeftIcon class="h-6 w-6 text-blue-600 dark:text-blue-400" />
                   </div>
                   <div class="ml-4">
                      <p class="text-sm font-medium text-gray-500 dark:text-gray-400">Message</p>
                      <p class="text-md text-gray-900 dark:text-gray-100 break-words">{{ reservation.message || 'No message' }}</p>
                   </div>
                </div>
             </div>
          </div>

          <!-- Actions -->
          <div v-if="canShowActions" class="mt-8 flex gap-4 justify-center">
             <button
                @click="handleEditClick"
                class="flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 transition-colors shadow-sm"
              >
                <PencilIcon class="h-5 w-5 mr-2" />
                Update Reservation
              </button>
              
              <button
                @click="handleCancelClick"
                class="flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600 transition-colors shadow-sm"
              >
                <TrashIcon class="h-5 w-5 mr-2" />
                Cancel Reservation
              </button>
          </div>
          
           <!-- Expired Message -->
           <div v-else-if="isExpired" class="mt-8 text-center p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700">
               <p class="text-gray-500 dark:text-gray-400 font-medium">Cannot update reservation as it has expired.</p>
           </div>
          
          <div v-if="reservation.status === 'CANCELLED'" class="mt-6 text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
             <p class="text-red-700 dark:text-red-300 font-medium">This reservation has been cancelled.</p>
          </div>
    </div>


    <!-- FORM MODE (Create OR Edit) -->
    <div v-if="isCreateMode || isEditMode" id="reservation-form" class="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 transition-colors duration-300">
       
       <div v-if="isEditMode" class="mb-6 flex justify-between items-center">
          <h3 class="text-xl font-bold text-gray-900 dark:text-gray-100">Update Reservation</h3>
          <button @click="cancelEdit" class="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
             <XMarkIcon class="h-6 w-6" />
          </button>
       </div>

       <form class="space-y-4" @submit.prevent="showConfirmationModal">
          
          <div :class="{'p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg': isEditMode}">
             <!-- Row 1: Name & Email -->
             <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                 <!-- Name -->
                 <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Name</label>
                    <div class="relative rounded-lg shadow-sm">
                      <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <UserIcon class="h-5 w-5 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                      </div>
                      <input
                        v-model="name"
                        type="text"
                        :readonly="isEditMode"
                        :disabled="isEditMode"
                        :placeholder="isEditMode ? '' : 'Enter name'"
                        class="block w-full appearance-none rounded-lg border pl-10 px-3 py-2.5 text-sm transition-all duration-200 focus:outline-none"
                        :class="[
                          isEditMode ? 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 cursor-not-allowed border-gray-300 dark:border-gray-600' : 
                          'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:border-violet-500 focus:ring focus:ring-violet-200 dark:focus:ring-violet-800/20',
                          {'border-red-600 dark:border-red-500': submitted && v$.name && v$.name.$error}
                        ]"
                      />
                    </div>
                     <div v-if="isCreateMode && submitted && v$.name.$error" class="mt-1 text-xs text-red-600 dark:text-red-400">{{ v$.name.$errors[0].$message }}</div>
                 </div>

                 <!-- Email -->
                 <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Email</label>
                    <div class="relative rounded-lg shadow-sm">
                      <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <EnvelopeIcon class="h-5 w-5 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                      </div>
                      <input
                        v-model="emailId"
                        type="email"
                        :readonly="isEditMode"
                        :disabled="isEditMode"
                        :placeholder="isEditMode ? '' : 'Enter email'"
                        class="block w-full appearance-none rounded-lg border pl-10 px-3 py-2.5 text-sm transition-all duration-200 focus:outline-none"
                        :class="[
                          isEditMode ? 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 cursor-not-allowed border-gray-300 dark:border-gray-600' : 
                          'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:border-violet-500 focus:ring focus:ring-violet-200 dark:focus:ring-violet-800/20',
                          {'border-red-600 dark:border-red-500': submitted && v$.emailId && v$.emailId.$error}
                        ]"
                      />
                    </div>
                    <div v-if="isCreateMode && submitted && v$.emailId.$error" class="mt-1 text-xs text-red-600 dark:text-red-400">{{ v$.emailId.$errors[0].$message }}</div>
                 </div>
             </div>

             <!-- Row 2: Phone & Guests -->
             <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                 <!-- Phone -->
                 <div v-if="isCreateMode">
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Phone</label>
                    <div class="relative rounded-lg shadow-sm">
                      <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <PhoneIcon class="h-5 w-5 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                      </div>
                      <input
                        v-model="phoneNumber"
                        type="text"
                        placeholder="Enter phone number"
                        class="block w-full appearance-none rounded-lg border pl-10 px-3 py-2.5 text-sm transition-all duration-200 focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:border-violet-500 focus:ring focus:ring-violet-200"
                        :class="{'border-red-600 dark:border-red-500': submitted && v$.phoneNumber && v$.phoneNumber.$error}"
                      />
                    </div>
                    <div v-if="isCreateMode && submitted && v$.phoneNumber.$error" class="mt-1 text-xs text-red-600 dark:text-red-400">{{ v$.phoneNumber.$errors[0].$message }}</div>
                 </div>
                 
                  <div v-else>
                     <label class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Phone</label>
                    <div class="relative rounded-lg shadow-sm">
                      <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <PhoneIcon class="h-5 w-5 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                      </div>
                       <input
                        v-model="phoneNumber"
                        type="text"
                        readonly
                        disabled
                        class="block w-full appearance-none rounded-lg border pl-10 px-3 py-2.5 text-sm transition-all duration-200 focus:outline-none bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 cursor-not-allowed border-gray-300 dark:border-gray-600"
                      />
                    </div>
                  </div>

                 <!-- Guests -->
                 <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Guests</label>
                    <div class="relative rounded-lg shadow-sm">
                      <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <UserGroupIcon class="h-5 w-5 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                      </div>
                      <select
                        v-model="selectedGuest"
                        class="block w-full appearance-none rounded-lg border pl-10 px-3 py-2.5 text-sm transition-all duration-200 focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:border-violet-500 focus:ring focus:ring-violet-200"
                        :class="{'border-red-600 dark:border-red-500': submitted && v$.selectedGuest.$error}"
                      >
                        <option v-for="g in guests" :key="g.id" :value="g.value">{{ g.value }}</option>
                      </select>
                    </div>
                 </div>
             </div>

             <!-- Row 3: Date & Time -->
             <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                 <!-- Date -->
                 <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Date</label>
                    <div
                      class="rounded-lg"
                      :class="{'ring-1 ring-red-600 dark:ring-red-500': submitted && v$.date.$error}"
                    >
                      <VueDatePicker
                        v-model="date"
                        :locale="datePickerLocale"
                        format="dd/MM/yyyy"
                        model-type="yyyy-MM-dd"
                        :min-date="minReservationDate"
                        :max-date="maxReservationDate"
                        auto-apply
                        :class="{'ring-1 ring-red-600 dark:ring-red-500': submitted && v$.date.$error}"
                        input-class-name="'block w-full appearance-none rounded-lg border pl-10 px-3 py-2.5 text-sm transition-all duration-200 focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:border-violet-500 focus:ring focus:ring-violet-200'"
                        size="medium"
                        :dark="isDatePickerDark"
                        :enable-time-picker="false"
                      />
                    </div>
                    <div v-if="submitted && v$.date.$error" class="mt-1 text-xs text-red-600 dark:text-red-400">{{ v$.date.$errors[0].$message }}</div>
                 </div>

                  <!-- Time -->
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Time</label>
                    <div class="relative rounded-lg shadow-sm" :class="{'ring-1 ring-red-600 dark:ring-red-500': submitted && v$.selectedTime.$error}">
                      <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <ClockIcon class="h-5 w-5 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                      </div>
                      <MobileTimePicker v-model="selectedTime" />
                    </div>
                     <div v-if="submitted && v$.selectedTime.$error" class="mt-1 text-xs text-red-600 dark:text-red-400">{{ v$.selectedTime.$errors[0].$message }}</div>
                 </div>
             </div>
             
             <!-- Row 4: Message (Full width) -->
             <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Message (optional)</label>
                <div class="relative rounded-lg shadow-sm">
                  <div class="absolute top-3 left-0 pl-3 flex items-start pointer-events-none">
                    <ChatBubbleLeftIcon class="h-5 w-5 text-gray-400 dark:text-gray-500" aria-hidden="true" />
                  </div>
                  <textarea
                    v-model="message"
                    rows="3"
                    class="block w-full appearance-none rounded-lg border pl-10 px-3 py-2.5 text-sm transition-all duration-200 focus:outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600 focus:border-violet-500 focus:ring focus:ring-violet-200"
                  ></textarea>
                </div>
             </div>

          </div>

           <!-- Buttons -->
           <div class="flex justify-center text-center mt-5 gap-3">
              <button
                v-if="isEditMode"
                type="button"
                @click="cancelEdit"
                class="flex-1 max-w-xs rounded-lg border border-transparent bg-gray-400 dark:bg-gray-600 py-2.5 px-4 text-sm font-medium text-white shadow-md hover:bg-gray-500 dark:hover:bg-gray-500 focus:outline-none transition-all duration-200"
              >
                Cancel
              </button>
              
              <button
                type="submit"
                class="flex-1 max-w-xs justify-center rounded-lg border border-transparent bg-violet-600 dark:bg-violet-600 py-2.5 px-4 text-sm font-medium text-white shadow-md hover:bg-violet-700 dark:hover:bg-violet-700 focus:outline-none transition-all duration-200"
              >
                <span v-if="isEditMode">Save Changes</span>
                <span v-else>Book Table</span>
              </button>
           </div>
       </form>
    </div>

    
  </div>
  
  <LoadingSpinner :isLoading="isLoading" />
  
  <!-- CONFIRMATION MODAL -->
  <TransitionRoot as="template" :show="showConfirmModal">
    <Dialog as="div" class="relative z-50" @close="closeConfirmModal">
      <TransitionChild as="div" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
      </TransitionChild>

      <div class="fixed inset-0 z-20 overflow-y-auto">
        <div class="flex min-h-full justify-center p-4 text-center items-center sm:p-0">
          <TransitionChild as="div" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <DialogPanel class="relative transform rounded-lg bg-white dark:bg-gray-800 px-6 pt-6 pb-6 text-left shadow-xl transition-all my-8 w-full max-w-md">
              <div class="absolute top-0 right-0 pt-3 pr-3 block">
                <button type="button" class="rounded-full border border-gray-300 dark:border-gray-600 p-1 bg-white dark:bg-gray-700 text-gray-400 dark:text-gray-300 hover:text-gray-600 dark:hover:text-gray-100 focus:outline-none transition-colors duration-200" @click="closeConfirmModal">
                  <span class="sr-only">Close</span>
                  <XMarkIcon class="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
              
              <div class="text-center">
                <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-violet-100 dark:bg-violet-900/20 mb-4">
                  <CalendarIcon class="h-6 w-6 text-violet-600 dark:text-violet-400" aria-hidden="true" />
                </div>
                <h3 class="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                    {{ isEditMode ? 'Confirm Changes' : 'Confirm Booking' }}
                </h3>
                <div class="mb-6 text-left bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4">
                  <div class="space-y-2 text-sm">
                    <div v-if="isCreateMode" class="flex justify-between">
                      <span class="text-gray-600 dark:text-gray-400">Name:</span>
                      <span class="font-medium text-gray-900 dark:text-gray-100">{{ pendingReservationData?.name }}</span>
                    </div>
                    <div class="flex justify-between">
                      <span class="text-gray-600 dark:text-gray-400">Date:</span>
                      <span class="font-medium text-gray-900 dark:text-gray-100">{{ pendingReservationData?.date }}</span>
                    </div>
                    <div class="flex justify-between">
                      <span class="text-gray-600 dark:text-gray-400">Time:</span>
                      <span class="font-medium text-gray-900 dark:text-gray-100">
                        {{ time_option.find(t => t.value === pendingReservationData?.time)?.time || pendingReservationData?.time }}
                      </span>
                    </div>
                     <div class="flex justify-between">
                      <span class="text-gray-600 dark:text-gray-400">Guests:</span>
                      <span class="font-medium text-gray-900 dark:text-gray-100">{{ pendingReservationData?.guests }}</span>
                    </div>
                    <div v-if="pendingReservationData?.message" class="pt-2 border-t border-gray-200 dark:border-gray-700">
                      <span class="text-gray-600 dark:text-gray-400">Message:</span>
                      <p class="mt-1 text-gray-900 dark:text-gray-100">{{ pendingReservationData?.message }}</p>
                    </div>
                  </div>
                </div>
                <div class="flex justify-center space-x-3">
                  <button @click="closeConfirmModal" :disabled="isLoading" class="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-2.5 px-4 text-sm font-medium text-gray-700 dark:text-gray-200 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none transition-all duration-200 disabled:opacity-50">
                    Cancel
                  </button>
                  <button @click="handleSubmit" :disabled="isLoading" class="rounded-lg border border-transparent bg-violet-600 dark:bg-violet-600 py-2.5 px-4 text-sm font-medium text-white shadow-sm hover:bg-violet-700 dark:hover:bg-violet-700 focus:outline-none transition-all duration-200 disabled:opacity-50">
                    <span v-if="isLoading">Processing...</span>
                    <span v-else>Confirm</span>
                  </button>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>

  <!-- Cancel Confirmation Modal -->
  <TransitionRoot as="template" :show="showCancelModal">
    <Dialog as="div" class="relative z-50" @close="closeCancelModal">
      <TransitionChild as="div" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
      </TransitionChild>

      <div class="fixed inset-0 z-20 overflow-y-auto">
        <div class="flex min-h-full justify-center p-4 text-center items-center sm:p-0">
          <TransitionChild as="div" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <DialogPanel class="relative transform rounded-lg bg-white dark:bg-gray-800 px-6 pt-6 pb-6 text-left shadow-xl transition-all my-8 w-full max-w-md">
              <div class="absolute top-0 right-0 pt-3 pr-3 block">
                <button type="button" class="rounded-full border border-gray-300 dark:border-gray-600 p-1 bg-white dark:bg-gray-700 text-gray-400 dark:text-gray-300 hover:text-gray-600 dark:hover:text-gray-100 focus:outline-none transition-colors duration-200" @click="closeCancelModal">
                  <span class="sr-only">Close</span>
                  <XMarkIcon class="h-5 w-5" aria-hidden="true" />
                </button>
              </div>
              
              <div class="text-center">
                <div class="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20 mb-4">
                  <XCircleIcon class="h-6 w-6 text-red-600 dark:text-red-400" aria-hidden="true" />
                </div>
                <h3 class="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Cancel Reservation</h3>
                <div class="mb-6">
                  <p class="text-sm text-gray-500 dark:text-gray-400">
                    Are you sure you want to cancel this reservation? This action cannot be undone.
                  </p>
                </div>
                <div class="flex justify-center space-x-3">
                  <button @click="closeCancelModal" :disabled="isProcessing" class="rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-2.5 px-4 text-sm font-medium text-gray-700 dark:text-gray-200 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none transition-all duration-200 disabled:opacity-50">
                    Keep Reservation
                  </button>
                  <button @click="confirmCancel" :disabled="isProcessing" class="rounded-lg border border-transparent bg-red-600 dark:bg-red-600 py-2.5 px-4 text-sm font-medium text-white shadow-sm hover:bg-red-700 dark:hover:bg-red-700 focus:outline-none transition-all duration-200 disabled:opacity-50">
                    <span v-if="isProcessing">Cancelling...</span>
                    <span v-else>Cancel Reservation</span>
                  </button>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<style scoped>
input[type="date"] {
  color-scheme: light dark;
}
.transition-colors {
  transition-property: color, background-color, border-color, text-decoration-color, fill, stroke;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 300ms;
}
input:focus, textarea:focus, select:focus {
  @apply ring-2;
}
input[type="date"]::-webkit-calendar-picker-indicator {
  @apply dark:filter dark:invert;
  margin-left: 0.5rem;
}
select {
  text-indent: 0;
  padding-left: 2.5rem;
}
input[type="date"] {
  padding-left: 2.5rem;
}
input[type="date"]::-webkit-calendar-picker-indicator {
    background: transparent;
    bottom: 0;
    color: transparent;
    cursor: pointer;
    height: auto;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    width: auto;
}
.dp__theme_light {
  --dp-background-color: #ffffff;
  --dp-text-color: #111827;
  --dp-hover-color: #f3f4f6;
  --dp-hover-text-color: #111827;
  --dp-border-color: #d1d5db;
  --dp-menu-border-color: #d1d5db;
  --dp-primary-color: #7c3aed;
  --dp-primary-text-color: #ffffff;
  --dp-icon-color: #6b7280;
  --dp-border-radius: 0.5rem;
}

.dp__theme_dark {
  --dp-background-color: #374151;
  --dp-text-color: #f3f4f6;
  --dp-hover-color: #4b5563;
  --dp-hover-text-color: #f3f4f6;
  --dp-border-color: #4b5563;
  --dp-menu-border-color: #4b5563;
  --dp-primary-color: #8b5cf6;
  --dp-primary-text-color: #ffffff;
  --dp-icon-color: #9ca3af;
  --dp-border-radius: 0.5rem;
  --dp-font-size: 0.875rem;
  --dp-line-height: 1.25rem;
  --dp-padding-top: 0.625rem;
  --dp-padding-bottom: 0.625rem;
}
.dp__main .dp__input{
  --dp-font-size: 0.9rem;
  --dp-line-height: 1.25rem;
  padding-top: 0.625rem;
  padding-bottom: 0.625rem;
  border-radius: 0.5rem;
}
</style>
